import { memo, useEffect } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { z } from "zod"

import type { CompaniesGet } from "@productlane/api"
import { isValidDomain } from "@productlane/lib/src/domain"
import { Button, Dialog, Form, FormInput, useZodForm } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { generateId } from "@/lib/id"
import { useSession } from "@/lib/session"
import { mutateOk, useZero } from "@/lib/zero"
import { findCompanyByDomain, findCompanyByName } from "./customer-domains"

export type PartialCustomerCreate = Pick<
	CompaniesGet[number],
	"id" | "name" | "domains"
>

interface CreateCustomerDialogProps {
	isOpen: boolean
	onOpenChange: (
		value: boolean,
		created: PartialCustomerCreate | undefined,
	) => void
	defaultName?: string
}

const CreateCustomerFormSchema = z.object({
	name: z.string().trim().min(1, "Enter a name for this customer."),
	domain: z
		.string()
		.trim()
		.min(1, "Enter a domain for this customer.")
		.refine(isValidDomain, 'Enter a domain like "acme.com".'),
})

export const CreateCustomerDialog = memo(
	({ isOpen, onOpenChange, defaultName }: CreateCustomerDialogProps) => {
		const isCustomersEnabled = api.linear.customersEnabled.useQuery(undefined, {
			cacheTime: Infinity,
			staleTime: Infinity,
		})
		const navigate = useNavigate()
		const form = useZodForm({
			schema: CreateCustomerFormSchema,
			defaultValues: { name: defaultName },
		})

		useEffect(() => {
			if (isOpen) {
				// init because tagGroups are not defined on first render
				form.resetField("name", { defaultValue: defaultName })
				form.setValue("name", defaultName ?? "")
			} else {
				form.reset()
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [form, isOpen])
		const zero = useZero()
		const { session } = useSession()
		const [companies] = useQuery(queries.companies({}), { enabled: isOpen })

		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={(value) => {
					onOpenChange(value, undefined)
				}}
				title="Create Customer"
				footer={
					<Button form="create-company-form" type="submit">
						Submit
					</Button>
				}
				onCloseAutoFocus={(event) => {
					event.preventDefault()
					document.body.style.pointerEvents = ""
				}}
				onInteractOutside={(event) => event.preventDefault()}
			>
				<Form
					id="create-company-form"
					form={form}
					className="space-y-4"
					onSubmit={async (
						values: z.infer<typeof CreateCustomerFormSchema>,
					) => {
						if (!session) {
							return
						}
						const domain = values.domain.trim().toLowerCase()
						const payload = {
							id: generateId(),
							domains: [domain],
							name: values.name.trim(),
						}

						const existingCustomer = findCompanyByDomain(companies, domain)
						if (existingCustomer) {
							form.setError("domain", {
								message: `"${domain}" already belongs to ${existingCustomer.name ?? "another customer"}.`,
							})
							return
						}

						const existingCustomerByName = findCompanyByName(
							companies,
							payload.name,
						)
						if (existingCustomerByName) {
							form.setError("name", {
								message: `A customer named "${payload.name}" already exists.`,
							})
							return
						}

						if (!isCustomersEnabled.data && !isCustomersEnabled.isLoading) {
							toast.info({
								title: "Linear customers are disabled",
								description:
									"It seems that Customers in Linear are disabled or your plan is outdated. Please enable them in Linear's settings or upgrade your plan to avoid issues with creating customers.",
								action: {
									label: "Open Linear settings",
									callback: () =>
										window.open(
											"https://linear.app/settings/customers-requests",
											"_blank",
										),
								},
							})
						}
						await mutateOk(
							zero.mutate(
								mutators.companies.create({
									...payload,
									autoAdd: true,
									intercomId: null,
									hubspotId: null,
									attioId: null,
									productboardId: null,
									domain: payload.domains[0] ?? "",
									logoUrl: null,
									linearCustomerId: null,
									size: null,
									revenue: null,
									statusId: null,
									statusName: null,
									statusColor: null,
									tierId: null,
									tierName: null,
									linearOwnerId: null,
									linearOwnerName: null,
									linearOwnerAvatarUrl: null,
								}),
							),
						)
						onOpenChange(false, payload)
						form.reset()
						toast.success({
							title: "Customer created",
							descriptionAction: {
								label: payload.name,
								callback: () =>
									void navigate({
										to: "/customers/$id",
										params: { id: payload.id },
									}),
							},
							description: "created",
						})
						analytics.track("create-company")
						void navigate({ to: "/customers/$id", params: { id: payload.id } })
					}}
				>
					<FormInput
						name="name"
						label="Name"
						placeholder="Productlane"
						className="px-px"
					/>
					<FormInput
						name="domain"
						label="Domain"
						placeholder="productlane.com"
						className="px-px"
					/>
				</Form>
			</Dialog>
		)
	},
)
